import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { MFA_LEVEL_LABEL, isMfaLevel } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { SelectField, TextField } from '../components/Fields.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { createTenant, dateText, daysAgo, loadTenants, type AdminTenant } from './api.ts';
import { AdminTenantDetail } from './AdminTenantDetail.tsx';
import { WebCheckPanel } from './WebCheckPanel.tsx';
import { TableWrap } from '../components/TableWrap.tsx';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const SLUG = /^[a-z0-9][a-z0-9-]{1,62}$/;
const STATUS_LABEL: Record<AdminTenant['status'], string> = {
  active: 'Active',
  suspended: 'Suspended',
  closed: 'Closed',
};

/** Braynewave's console: every organisation on Teslyn, and one organisation's detail. */
export function AdminTenantsScreen({ sb }: { readonly sb: SupabaseClient }): ReactElement {
  const [tenants, setTenants] = useState<AdminTenant[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(() => {
    setError(null);
    loadTenants(sb)
      .then(setTenants)
      .catch((e: unknown) => setError(message(e)));
  }, [sb]);
  useEffect(reload, [reload]);

  const current = tenants?.find((t) => t.id === open) ?? null;
  if (current) {
    return (
      <AdminTenantDetail
        sb={sb}
        tenant={current}
        onBack={() => {
          setOpen(null);
          reload();
        }}
        onChanged={reload}
      />
    );
  }

  return (
    <section className="page">
      <PageHeader
        title="Tenants"
        subtitle="Every organisation on Teslyn. Braynewave only; every change is recorded."
      >
        <Button variant="primary" onClick={() => setAdding(true)} disabled={adding}>
          Add organisation
        </Button>
      </PageHeader>
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {adding && (
        <AddTenant
          onCancel={() => setAdding(false)}
          onAdd={async (t) => {
            await createTenant(sb, t);
            setAdding(false);
            setNotice(
              `${t.name} added. Set its licence, then invite its first tenant admin from Users & roles on its portal.`,
            );
            reload();
          }}
        />
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!tenants && !error && <Loading what="organisations" />}
      {tenants && <WebCheckPanel sb={sb} tenants={tenants} />}
      {tenants && (
        <TableWrap>
          <table className="table">
            <thead>
              <tr>
                <th className="label">Organisation</th>
                <th className="label">Status</th>
                <th className="label">Licence</th>
                <th className="label">Seats</th>
                <th className="label">People</th>
                <th className="label">Two-step</th>
                <th className="label">Last export</th>
                <th className="label">Unpaid invoices</th>
                <th className="label">Last activity</th>
                <th className="label">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {tenants.length === 0 && (
                <tr>
                  <td colSpan={10} className="muted">
                    No organisations yet.
                  </td>
                </tr>
              )}
              {tenants.map((t) => (
                <tr key={t.id}>
                  <td>
                    {t.name}
                    <br />
                    <span className="caption muted data">{t.slug}</span>
                  </td>
                  <td>{STATUS_LABEL[t.status]}</td>
                  <td>
                    {t.plan === null ? (
                      <span className="muted">None</span>
                    ) : (
                      <>
                        {t.plan}
                        {t.licenceStatus === 'cancelled' ? ' (cancelled)' : ''}
                        {t.subscriptionEnds && (
                          <span className="caption muted"> · ends {dateText(t.subscriptionEnds)}</span>
                        )}
                      </>
                    )}
                  </td>
                  <td className="data">{t.seats === null ? '–' : `${t.seatsInUse} of ${t.seats}`}</td>
                  <td>
                    {t.staff} staff, {t.clientUsers} client{t.clientUsers === 1 ? '' : 's'}
                    <br />
                    <span className="caption muted">{t.projects} projects</span>
                  </td>
                  <td>
                    {isMfaLevel(t.mfaRequired) && t.mfaRequired !== 'off'
                      ? MFA_LEVEL_LABEL[t.mfaRequired]
                      : 'Not required'}
                  </td>
                  <td>
                    {t.lastExportAt ? (
                      <>
                        {t.lastExportStatus === 'failed' ? 'Failed' : 'Done'}{' '}
                        <span className="caption muted">{daysAgo(t.lastExportAt)}</span>
                      </>
                    ) : (
                      <span className="muted">None yet</span>
                    )}
                  </td>
                  <td className="data">{t.unpaidInvoices}</td>
                  <td className="caption">{daysAgo(t.lastActivityAt)}</td>
                  <td className="table__actions">
                    <Button variant="quiet" aria-label={`Open ${t.name}`} onClick={() => setOpen(t.id)}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </section>
  );
}

function AddTenant({
  onAdd,
  onCancel,
}: {
  readonly onAdd: (t: { slug: string; name: string; region: string }) => Promise<void>;
  readonly onCancel: () => void;
}): ReactElement {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [region, setRegion] = useState('ap-southeast-2');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!name.trim()) return setError('Enter the organisation’s name.');
    if (!SLUG.test(slug))
      return setError('The address name is 2 to 63 lower-case letters, digits and hyphens.');
    setBusy(true);
    setError(null);
    try {
      await onAdd({ name: name.trim(), slug, region });
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel stack" onSubmit={(e) => void submit(e)} noValidate aria-label="Add organisation">
      <div className="form-grid">
        <TextField label="Name" value={name} onChange={setName} autoFocus />
        <TextField
          label="Address name"
          spellCheck={false}
          hint="Its portal address, for example thomson-airborne"
          value={slug}
          onChange={(v) => setSlug(v.toLowerCase())}
        />
        <SelectField
          label="Data region"
          value={region}
          onChange={setRegion}
          options={[['ap-southeast-2', 'Sydney (ap-southeast-2)']]}
        />
      </div>
      {error && (
        <p className="field__error caption" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <Button type="submit" variant="primary" busy={busy}>
          Add organisation
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
